import {useMemo, useRef, useState} from "react";
import {Alert, Box, Button, Checkbox, Chip, FormControlLabel, Grid} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import Loading from "../Loading";
import AudienceParameterRow from "./AudienceParameterRow";
import {hasRangeError, indexAfterDelete, indexParameters} from "./audienceEditorModel";

const chipStyle = {borderRadius: "5px", height: '40px', width: '100%'};
const toolButtonStyle = {height: '40px'};

/**
 * Редактор аудиторий: вкладки-аудитории, строки условий, расширенные настройки.
 * Используется AudienceStep кампаний (форма, шаблоны, bulk update) и AB-тестов — различия в variant.
 *
 * Обработчики передаются с прежними сигнатурами (handleParameterValueChange(value, paramIndex, audienceIndex) и т.д.).
 * item — кампания / AB-тест: {audiences, fickle, repeatable}; setItem принимает функцию-апдейтер.
 */
export default function AudienceEditor({
                                           item, setItem, handlers, params, paramsQuery, variant, abbreviations,
                                           errorAudienceIndexes, errorFields, showRepeatable,
                                       }) {
    const [selected, setSelected] = useState(0);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const audiences = item.audiences;
    // аудиторию могли удалить снаружи — не выходим за границы
    const currentIndex = Math.min(selected, audiences.length - 1);
    const currentAudience = audiences[currentIndex];

    // актуальные обработчики родителя без пересоздания колбэков строк (иначе memo строк бесполезен)
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const actions = useMemo(() => ({
        parameterChange: (...args) => handlersRef.current.handleParameterDataChange(...args),
        operatorChange: (...args) => handlersRef.current.handleParameterOperatorChange(...args),
        valueChange: (...args) => handlersRef.current.handleParameterValueChange(...args),
        manualInput: (...args) => handlersRef.current.handleManualInput(...args),
        offsetChange: (...args) => handlersRef.current.handleManualInputForOffset(...args),
        rangeChange: (...args) => handlersRef.current.handleVersionNumericParameterChange(...args),
        deleteParameter: (...args) => handlersRef.current.handleDeleteParams(...args),
    }), []);

    const paramsIndex = useMemo(() => indexParameters(params), [params]);
    const errorIndexes = useMemo(() => new Set(errorAudienceIndexes || []), [errorAudienceIndexes]);

    const addAudience = () => {
        handlers.handleAddNewAudience(audiences.length);
        setSelected(audiences.length);
    };
    const cloneAudience = () => {
        handlers.handleCloneAudience(currentIndex);
        setSelected(audiences.length);
    };
    const deleteAudience = (index) => {
        handlers.handleDeleteAudience(index);
        setSelected(indexAfterDelete(currentIndex, index));
    };

    return (
        <Grid container spacing={3} minWidth={variant.minWidth}>
            <Grid item xs={12} container spacing={1}>
                {audiences.map((audience, index) => {
                    const hasError = errorIndexes.has(index);
                    return <Grid item key={audience.name + index} xs={2}>
                        <Chip
                            icon={hasError ? <WarningRoundedIcon/> : undefined}
                            style={chipStyle}
                            variant={index === currentIndex ? 'filled' : 'outlined'}
                            label={audience.name}
                            color={hasError ? 'error' : 'warning'}
                            onClick={() => setSelected(index)}
                            onDelete={audiences.length > 1 ? () => deleteAudience(index) : undefined}
                        />
                    </Grid>;
                })}
                <Grid item>
                    <Button variant={'outlined'} color={'info'} size={'small'} fullWidth style={toolButtonStyle} onClick={addAudience}>
                        <AddIcon sx={{mr: 0.5}}/>
                        Add
                    </Button>
                </Grid>
                {handlers.handleCloneAudience &&
                    <Grid item>
                        <Button variant={'outlined'} color={variant.cloneButtonColor} size={'small'} fullWidth style={toolButtonStyle} onClick={cloneAudience}>
                            <ContentCopyIcon sx={{mr: 0.5}}/>
                            Clone
                        </Button>
                    </Grid>}
            </Grid>

            <Grid item xs={12}>
                <Loading isLoading={paramsQuery.isLoading} isError={paramsQuery.isError} error={paramsQuery.error}>
                    <Grid container spacing={1}>
                        {currentAudience?.parameters && paramsQuery.status === 'fulfilled' &&
                            currentAudience.parameters.map((parameterSet, paramIndex) => (
                                <AudienceParameterRow
                                    key={paramIndex}
                                    parameterSet={parameterSet}
                                    paramIndex={paramIndex}
                                    audienceIndex={currentIndex}
                                    canDelete={currentAudience.parameters.length > 1}
                                    params={params}
                                    paramsIndex={paramsIndex}
                                    variant={variant}
                                    abbreviations={abbreviations}
                                    rangeError={hasRangeError(errorFields, currentIndex, paramIndex, parameterSet?.parameterData?.parameterName)}
                                    actions={actions}
                                />
                            ))}
                        <Grid item xs={4} pt={2} pl={1}>
                            <Button variant={'outlined'} aria-label="Add condition" onClick={() => handlers.handleAddNewParams(currentIndex)}>
                                <AddIcon/>
                            </Button>
                        </Grid>
                    </Grid>
                </Loading>
            </Grid>

            <Grid item xs={12} sx={{pt: '3px !important'}} display={'flex'} justifyContent={'center'}>
                <span style={{textDecoration: 'underline dotted', cursor: 'pointer'}}
                      onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}>
                    advanced settings
                </span>
            </Grid>

            {showAdvancedSettings &&
                <Grid item xs={12}>
                    <Box>
                        <FormControlLabel
                            label="Check Audiences Every Time (fickle)"
                            control={<Checkbox
                                name="fickle"
                                checked={!!item.fickle}
                                onChange={() => setItem(current => ({...current, fickle: !current.fickle}))}
                            />}
                        />
                        {variant.fickleWarning && item.fickle && (
                            <Alert severity="warning">
                                This feature is deprecated. We recommend not using it without first discussing it with the backend team.
                            </Alert>
                        )}
                    </Box>
                    {showRepeatable &&
                        <Box>
                            <FormControlLabel
                                label="Allow repeat after finish (repeatable)"
                                control={<Checkbox
                                    name="repeatable"
                                    checked={!!item.repeatable}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setItem(current => ({...current, repeatable: checked}));
                                    }}
                                />}
                            />
                        </Box>}
                </Grid>}
        </Grid>
    );
}
